import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import axiosInstance from '../../api/axios';
import { toast } from 'react-toastify';
import { ChevronLeft } from 'lucide-react';
import Pagination from '../../components/Pagination';
import SortableTableHeader from '../../components/SortableTableHeader';

const labelCls = 'block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5';
const inputCls = 'w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30';

const DailyLotteryParticipants = () => {
  const { token } = useSelector((state) => state.auth);
  const { lotteryId } = useParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ page: 1, pageSize: 30, order: '' });
  const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRows: 0 });
  const [sort, setSort] = useState({ key: '', direction: 'asc' });
  const [sortedData, setSortedData] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filters.page) params.page = filters.page;
      if (filters.pageSize) params.pageSize = filters.pageSize;
      if (filters.order) params.order = filters.order;

      const response = await axiosInstance.get(
        `/api/admin/lottery/daily-lottery/${lotteryId}/participants`,
        { headers: { token }, params }
      );

      if (response.data.success) {
        const newData = response.data.data.data;
        setData(newData);
        setSortedData(newData);
        setPagination({
          currentPage: parseInt(response.data.data.currentPage) || 1,
          totalPages: response.data.data.totalPages || 1,
          totalRows: response.data.data.totalRows || 0,
        });
      }
    } catch (error) {
      toast.error('Failed to fetch participants data');
    } finally {
      setLoading(false);
    }
  }, [token, lotteryId, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    if (!data.length) return;
    const sorted = [...data].sort((a, b) => {
      if (!sort.key) return 0;
      let aValue = a[sort.key] ?? '';
      let bValue = b[sort.key] ?? '';
      if (sort.key === 'ticketNumber' || sort.key === 'amount') {
        return sort.direction === 'asc'
          ? (parseFloat(aValue) || 0) - (parseFloat(bValue) || 0)
          : (parseFloat(bValue) || 0) - (parseFloat(aValue) || 0);
      }
      if (sort.key === 'createdAt' || sort.key === 'updatedAt') {
        return sort.direction === 'asc'
          ? new Date(aValue) - new Date(bValue)
          : new Date(bValue) - new Date(aValue);
      }
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      return sort.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    });
    setSortedData(sorted);
  }, [data, sort]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: key === 'page' ? value : 1 }));
  };

  const handleSort = (key) => {
    setSort((prev) => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
  };

  return (
    <div>
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
            <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
            Services
          </div>
          <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">Daily Lottery <span className="text-gradient-ginox">Participants</span></h1>
          <p className="text-sm text-muted-foreground font-mono">Lottery ID: {lotteryId}</p>
        </div>
        <Link
          to="/lottery/daily"
          className="flex items-center gap-1.5 rounded-md border border-border/70 bg-white/[0.02] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors shrink-0"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Back
        </Link>
      </div>

      <div className="mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Order</label>
            <select value={filters.order} onChange={(e) => handleFilterChange('order', e.target.value)} className={inputCls}>
              <option value="">Default</option>
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Page Size</label>
            <select value={filters.pageSize} onChange={(e) => handleFilterChange('pageSize', parseInt(e.target.value))} className={inputCls}>
              {[10, 15, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-4">
          <button onClick={() => setFilters({ page: 1, pageSize: 30, order: '' })}
            className="rounded-md border border-border/70 bg-white/[0.02] px-4 py-2 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors">
            Clear Filters
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr>
              <SortableTableHeader sortKey="sno" currentSort={sort} onSort={handleSort}>#</SortableTableHeader>
              <SortableTableHeader sortKey="username" currentSort={sort} onSort={handleSort}>Username</SortableTableHeader>
              <SortableTableHeader sortKey="lottery_id" currentSort={sort} onSort={handleSort}>Lottery ID</SortableTableHeader>
              <SortableTableHeader sortKey="type" currentSort={sort} onSort={handleSort}>Type</SortableTableHeader>
              <SortableTableHeader sortKey="amount" currentSort={sort} onSort={handleSort}>Amount</SortableTableHeader>
              <SortableTableHeader sortKey="createdAt" currentSort={sort} onSort={handleSort}>Created At</SortableTableHeader>
              <SortableTableHeader sortKey="updatedAt" currentSort={sort} onSort={handleSort}>Updated At</SortableTableHeader>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="py-12 text-center text-sm text-muted-foreground">Loading...</td></tr>
            ) : sortedData.length === 0 ? (
              <tr><td colSpan={7} className="py-12 text-center text-sm text-muted-foreground">No participants found</td></tr>
            ) : sortedData.map((item, index) => (
              <tr key={item._id} className="group hover:bg-white/[0.02] transition-colors">
                <td className="border-b border-border/40 px-5 py-3.5 text-sm font-mono text-muted-foreground">
                  {(filters.page - 1) * filters.pageSize + index + 1}
                </td>
                <td className="border-b border-border/40 px-5 py-3.5 text-sm font-mono text-foreground">{item.username}</td>
                <td className="border-b border-border/40 px-5 py-3.5 text-sm font-mono text-muted-foreground">{item.lottery_id}</td>
                <td className="border-b border-border/40 px-5 py-3.5 text-sm text-foreground">{item.type}</td>
                <td className="border-b border-border/40 px-5 py-3.5 text-sm font-mono text-foreground">{item.amount}</td>
                <td className="border-b border-border/40 px-5 py-3.5 text-sm font-mono text-muted-foreground">
                  {new Date(item.createdAt).toLocaleString()}
                </td>
                <td className="border-b border-border/40 px-5 py-3.5 text-sm font-mono text-muted-foreground">
                  {new Date(item.updatedAt).toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="border-t border-white/[0.08] px-4">
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          totalRows={pagination.totalRows}
          pageSize={filters.pageSize}
          onPageChange={(p) => handleFilterChange('page', p)}
        />
      </div>
    </div>
  );
};

export default DailyLotteryParticipants;
